const WIDGET_SELECTOR = 'purchase-options[id*="stayai-subscription-widget-"]';
const PRODUCT_DETAILS_PRICE_SELECTOR = '.product-details product-price div[ref="priceContainer"] span.price';
const STICKY_PRICE_SELECTOR = 'div.sticky-add-to-cart__price div[ref="priceContainer"] span.price';

const ALLOWED_PRICE_TAGS = new Set(['span', 's', 'b', 'small', 'em', 'strong']);

/**
 * @param {string} html
 * @returns {string}
 */
function sanitizePriceHtml(html) {
  const doc = new DOMParser().parseFromString(html, 'text/html');

  /**
   * @param {Node} node
   * @returns {string}
   */
  function walk(node) {
    if (node.nodeType === Node.TEXT_NODE) return node.textContent ?? '';
    if (node.nodeType !== Node.ELEMENT_NODE) return '';
    const tag = node.nodeName.toLowerCase();
    if (!ALLOWED_PRICE_TAGS.has(tag)) return '';
    let inner = '';
    for (const child of node.childNodes) inner += walk(child);
    return `<${tag}>${inner}</${tag}>`;
  }

  let out = '';
  for (const node of doc.body.childNodes) out += walk(node);
  return out;
}

/**
 * @param {Element} [widget]
 */
function isSubscriptionSelected(widget) {
  const root =
    widget && typeof widget.querySelector === 'function'
      ? widget
      : document.querySelector(WIDGET_SELECTOR);
  if (!root) return false;
  const checked = root.querySelector('input[name="purchaseType"]:checked');
  if (checked && checked instanceof HTMLInputElement) return checked.value === 'purchaseTypeSubscription';
  return Boolean(root.querySelector('label[for="purchaseTypeSubscription"].is-selected'));
}

/**
 * @param {boolean} useSubscriptionPrice
 * @returns {boolean} true if sync succeeded (StayAI elements found and prices applied)
 */
function syncPrices(useSubscriptionPrice) {
  const subPriceElm = document.querySelector(`${WIDGET_SELECTOR} [data-stayai-subscription-price]`);
  const otpPriceElm = document.querySelector(`${WIDGET_SELECTOR} [data-stayai-onetime-price]`);
  if (!subPriceElm && !otpPriceElm) return false;

  const subPrice = (subPriceElm?.innerHTML ?? '').trim();
  const otpPrice = (otpPriceElm?.innerHTML ?? '').trim();
  /* OTP chosen: use only otpPrice so we never show subscription price by mistake; may stay theme price until StayAI renders */
  const raw = useSubscriptionPrice ? (subPrice || otpPrice) : otpPrice;
  if (!raw) return false;
  const priceHtml = sanitizePriceHtml(raw);
  const displayIsSubscription = useSubscriptionPrice && !!subPrice;

  const targets = [
    ...document.querySelectorAll(PRODUCT_DETAILS_PRICE_SELECTOR),
    document.querySelector(STICKY_PRICE_SELECTOR),
  ].filter(Boolean);

  for (const el of targets) {
    el.innerHTML = priceHtml;

    const container = el.closest('div[ref="priceContainer"]');
    if (!container) continue;
    for (const cmp of container.querySelectorAll('.compare-at-price')) {
      const wrapper = cmp.closest('span[role="group"]') || cmp;
      if (wrapper instanceof HTMLElement) {
        wrapper.style.display = displayIsSubscription ? 'none' : '';
      }
    }
  }
  return true;
}

let syncGeneration = 0;

/**
 * @param {number} [remaining]
 * @param {number} [scheduledGeneration]
 */
function syncWithRetry(remaining = 15, scheduledGeneration = undefined) {
  const myId = scheduledGeneration ?? ++syncGeneration;
  if (scheduledGeneration !== undefined && syncGeneration !== myId) return;
  const useSubscriptionPrice = isSubscriptionSelected();
  if (syncPrices(useSubscriptionPrice) || remaining <= 0) return;
  setTimeout(() => syncWithRetry(remaining - 1, myId), 300);
}

window.addEventListener('stay-ai-subscription-changed', (e) => {
  requestAnimationFrame(() => {
    const detail = e instanceof CustomEvent && e.detail != null ? /** @type {{ sellingPlanId?: string | number }} */ (e.detail) : undefined;
    const sid = detail?.sellingPlanId;
    const useSubscriptionPrice =
      sid != null && sid !== '' && (typeof sid === 'string' || typeof sid === 'number');
    syncPrices(useSubscriptionPrice);
  });
});

document.addEventListener('variant:update', () => {
  setTimeout(() => syncWithRetry(), 500);
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => syncWithRetry());
} else {
  syncWithRetry();
}
